<template>
  <DialogForm
    :model-value="modelValue"
    title="Marquer comme « Ne répond pas »"
    size="medium"
    :loading="saving"
    :dirty="dirty"
    save-label="Enregistrer l'absence de réponse"
    save-icon="fas fa-check"
    saving-label="Enregistrement…"
    :save-disabled="!canConfirm"
    data-testid="no-answer-modal"
    @update:model-value="close"
    @close="close"
    @save="handleConfirm"
  >
    <!-- Email : deux options mutuellement exclusives, plus la personnalisation
         du message — plusieurs éléments sous un même intitulé, donc un
         DialogSection (M11) plutôt qu'un simple `label` de champ. -->
    <DialogSection title="Email au client">
      <RadioGroup
        v-model="emailVariant"
        variant="card"
        :columns="1"
        :options="emailVariantOptions"
      />

      <button type="button" class="customize-toggle" @click="showCustomize = !showCustomize">
        <i :class="showCustomize ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
        Personnaliser l'email
      </button>
      <div v-if="showCustomize" class="customize-box">
        <Textarea
          v-model="customMessage"
          :rows="5"
          placeholder="Message personnalisé…"
          hint="Ce texte remplace le corps du message. La formule d'appel, les coordonnées et la signature sont conservées."
          test-id="no-answer-custom-message"
        />
      </div>
    </DialogSection>

    <!-- Répondeur : un seul contrôle, son propre `label`/`required` suffit. -->
    <RadioGroup
      v-model="voicemailChoice"
      label="Répondeur"
      required
      variant="card"
      :columns="1"
      :options="voicemailOptions"
    />

    <Textarea
      v-model="comment"
      label="Commentaire"
      :rows="3"
      placeholder="Précisez le contexte de l'appel (optionnel)"
      test-id="no-answer-comment"
    />

    <Button
      variant="ghost"
      size="sm"
      icon="fas fa-circle-info"
      class="no-answer-hint"
      data-testid="no-answer-info"
      @click="showInfo = true"
    >
      Que se passe-t-il après l'enregistrement ?
    </Button>
  </DialogForm>

  <InfoModal
    v-model="showInfo"
    title="« Ne répond pas » : que se passe-t-il ensuite ?"
    size="large"
    @close="showInfo = false"
  >
    <NoAnswerInfo />
  </InfoModal>
</template>

<script setup>
/**
 * Fenêtre v2 de l'issue d'appel « Ne répond pas » (kit ui/dialog/, cf.
 * docs/MODALES-V2.md). ELLE NE FAIT AUCUN APPEL (M10) : la bannière d'appel
 * globale (App.vue) enregistre, cette fenêtre ne fait qu'émettre `confirm`.
 *
 * LE BOUTON D'ACTION NE PORTE PLUS « CONFIRMER » SEUL (M7, docs/MODALES-V2.md) :
 * un mot qui ne dit rien à qui vient d'ouvrir la fenêtre. Le titre passe aussi
 * aux guillemets français, cohérents avec ceux du reste du kit v2.
 */
import { computed, ref, watch } from 'vue'
import { Button, DialogForm, DialogSection, InfoModal, RadioGroup, Textarea } from '../ui'
import NoAnswerInfo from '../shared/NoAnswerInfo.vue'
import { useDialogGuardOn } from '../../composables/ui/useDialogGuard.js'
import { VOICEMAIL_OPTIONS } from '../../constants/commercial-call.js'

const DEFAULT_BODY = "Nous avons essayé de vous joindre aujourd'hui pour confirmer les informations concernant votre demande de devis et finaliser votre projet de formation."

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  saving: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const voicemailOptions = VOICEMAIL_OPTIONS

const emailVariantOptions = [
  { value: 'basic', label: 'Envoyer l\'email « Nous avons essayé de vous joindre »' },
  { value: 'basic_rdv', label: 'Envoyer l\'email « Nous avons essayé de vous joindre » et proposer un RDV téléphonique' }
]

const emailVariant = ref('basic')
const showCustomize = ref(false)
const customMessage = ref(DEFAULT_BODY)
const voicemailChoice = ref('left_message')
const comment = ref('')
const showInfo = ref(false)

const canConfirm = computed(() => Boolean(voicemailChoice.value))

// Ordre important : ce watcher doit s'exécuter AVANT celui posé par la garde
// (ci-dessous), pour qu'elle prenne son empreinte sur le formulaire déjà remis
// à zéro à l'ouverture, et non sur le reliquat de la fermeture précédente.
watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      emailVariant.value = 'basic'
      showCustomize.value = false
      customMessage.value = DEFAULT_BODY
      voicemailChoice.value = 'left_message'
      comment.value = ''
    }
  }
)

// `showCustomize` n'entre pas dans l'empreinte : ouvrir/fermer le panneau de
// personnalisation n'est pas une saisie perdue en soi (M21).
const { dirty } = useDialogGuardOn(
  computed(() => props.modelValue),
  computed(() => ({
    emailVariant: emailVariant.value,
    customMessage: customMessage.value,
    voicemailChoice: voicemailChoice.value,
    comment: comment.value
  }))
)

const close = () => emit('update:modelValue', false)

const handleConfirm = () => {
  if (!canConfirm.value) return
  emit('confirm', {
    emailVariant: emailVariant.value,
    customMessage: showCustomize.value && customMessage.value.trim() ? customMessage.value.trim() : null,
    voicemailChoice: voicemailChoice.value,
    comment: comment.value.trim()
  })
}
</script>

<style scoped>
.customize-toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  align-self: flex-start;
  background: transparent;
  border: none;
  padding: var(--spacing-xs) 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  cursor: pointer;
}

.customize-toggle i {
  font-size: 0.7rem;
  width: 10px;
}

.no-answer-hint {
  align-self: flex-start;
}

.customize-box {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}
</style>
